import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { useHubModuleWriteAny } from "@/lib/useHubModuleWrite";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { EM_DASH } from "@/lib/utils";

import { fetchActiveAnalysisTransfers, printAnalysisTransferLabel } from "./analysisTransfersApi";
import { qcIncidentPath } from "@/lib/qc/qcIncidentNav";

export function QualityAnalysisTransfersPage() {
  const writesAllowed = useHubModuleWriteAny(["quality", "manufacturing"]);
  const { showToast } = useHubToast();
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.qualityAnalysisLoans,
    documents: [{ id: "loan", label: "Loan", labelSize: "4x2", dpi: 300 }],
  });
  const listQuery = useQuery({
    queryKey: ["quality", "analysis-transfers", "active"],
    queryFn: fetchActiveAnalysisTransfers,
  });
  const reprintMutation = useMutation({
    mutationFn: (transferId: number) => printAnalysisTransferLabel(transferId, selectedPrinter.trim()),
    onSuccess: () => {
      showToast({ tone: "success", message: "Loan label sent to the printer." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not print the loan label.",
      });
    },
  });
  const rows = listQuery.data?.rows ?? [];
  const printerReady = selectedPrinter.trim() !== "";

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Units currently out for analysis. Print 4x2 loan labels or open the linked case or QC incident to mark a return.">
        Analysis loans
      </PageTitleWithHelp>
      {writesAllowed ? (
        <div className="max-w-md">
          <ZplLocationSelect
            id="quality-analysis-loans-location"
            value={station}
            onChange={setStation}
            unavailableMessage={unavailableMessage}
          
      documents={documents}
    />
        </div>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>Open loans ({rows.length})</CardTitle>
        </CardHeader>
        <CardBody>
          {listQuery.isLoading ? (
            <p className="text-sm text-slate-600">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-600">No units are currently out for analysis.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {rows.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center gap-3">
                  <Link className="font-mono text-sky-800 hover:underline" to={`/quality/analysis-loans/${row.id}`}>
                    {row.deviceLabelSerial}
                  </Link>
                  <span>{row.destinationName ?? EM_DASH}</span>
                  {writesAllowed ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!printerReady || reprintMutation.isPending}
                      onClick={() => reprintMutation.mutate(row.id)}
                    >
                      Print label
                    </Button>
                  ) : null}
                  {row.rmaId != null ? (
                    <Link className="text-sky-800 hover:underline" to={`/quality/rmas/${row.rmaId}`}>
                      RMA
                    </Link>
                  ) : null}
                  {row.qualityCaseId != null ? (
                    <Link className="text-sky-800 hover:underline" to={`/quality/cases/${row.qualityCaseId}`}>
                      Case {row.qualityCaseId}
                    </Link>
                  ) : null}
                  {row.investigationId != null && row.qualityCaseId != null ? (
                    <Link
                      className="text-sky-800 hover:underline"
                      to={`/quality/cases/${row.qualityCaseId}/investigations/${row.investigationId}/traveler`}
                    >
                      Traveler
                    </Link>
                  ) : null}
                  {row.qualityIncidentId != null ? (
                    <Link className="text-sky-800 hover:underline" to={qcIncidentPath(row.qualityIncidentId, "quality")}>
                      QC incident {row.qualityIncidentId}
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
